import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { Btn, Card, Input, Select, PageTitle, StatusChip, DemoBadge, Empty, Tag, cx } from "../components/kit";
import { Loading } from "../components/common";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtDate, fmtDateTime, fmtLocal, todayISO } from "../lib/format";

const DEFAULTS = { tab: "dogs", q: "", status: "", direction: "" };

export default function Practice() {
  const { t } = useTranslation();
  const { me, setMe } = useAuth();
  const [f, setF] = useState({ ...DEFAULTS, ...(me.user.prefs?.practice || {}) });
  const [rows, setRows] = useState(null);
  const first = useRef(true);

  useEffect(() => {
    const params = { q: f.q || undefined, status: f.status || undefined, direction: f.direction || undefined };
    const url = f.tab === "dogs" ? "/dogs" : "/programmes";
    if (f.tab === "closed") params.closed = true;
    const h = setTimeout(() => api.get(url, { params }).then((r) => setRows(r.data)), 200);
    if (!first.current) {
      api.put("/auth/profile", { prefs: { practice: f } }).then((r) => setMe(r.data)).catch(() => {});
    }
    first.current = false;
    return () => clearTimeout(h);
  }, [f]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => { setRows(null); setF({ ...f, [k]: e.target.value }); };
  const statuses = f.tab === "dogs" ? ["none", "draft", "active", "completed", "discontinued"] : f.tab === "closed" ? ["completed", "discontinued"] : ["draft", "active", "completed", "discontinued"];
  return (
    <div>
      <PageTitle title={t("practice.title")} sub={t("practice.sub")} right={<Link to="/dogs/new"><Btn testid="new-dog-btn"><Plus className="h-4 w-4" />{t("practice.new_dog")}</Btn></Link>} />
      <div role="tablist" className="mb-4 flex flex-wrap gap-2">
        {["dogs", "programmes", "closed"].map((k) => (
          <button key={k} role="tab" aria-selected={f.tab === k} data-testid={`practice-tab-${k}`} onClick={() => { setRows(null); setF({ ...f, tab: k, status: "" }); }}
            className={cx("min-h-[44px] rounded-xl border px-4 text-sm font-semibold", f.tab === k ? "bg-white border-primary" : "bg-transparent text-muted-foreground hover:bg-white")}>{t(`practice.tab_${k}`)}</button>
        ))}
      </div>
      <Card className="mb-5 grid gap-3 p-4 sm:grid-cols-[1fr_200px_200px]">
        <label className="relative block">
          <span className="sr-only">{t("practice.search")}</span>
          <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
          <Input data-testid="practice-search-input" className="pl-9" placeholder={t("practice.search_ph")} value={f.q} onChange={set("q")} />
        </label>
        <Select data-testid="practice-status-filter" aria-label={t("practice.status")} value={f.status} onChange={set("status")}>
          <option value="">{t("practice.all_statuses")}</option>
          {statuses.map((s) => <option key={s} value={s}>{s === "none" ? t("practice.no_programme") : t(`status.${s}`)}</option>)}
        </Select>
        <Select data-testid="practice-direction-filter" aria-label={t("practice.direction")} value={f.direction} onChange={set("direction")}>
          <option value="">{t("practice.all_directions")}</option>
          {["A", "B", "C", "D"].map((d) => <option key={d} value={d}>{d} — {t(`dir.${d}`)}</option>)}
        </Select>
      </Card>
      {!rows ? <Loading /> : (
        <>
          <p data-testid="practice-count" className="mb-3 text-sm font-semibold text-muted-foreground">{t("practice.count", { count: rows.length })}</p>
          {rows.length === 0 ? <Empty testid="practice-empty" title={t("practice.empty")} action={<Link to="/dogs/new"><Btn variant="soft">{t("practice.new_dog")}</Btn></Link>} /> : (
            <ul className="grid gap-3">
              {f.tab === "dogs" ? rows.map((d) => <DogRow key={d.id} d={d} tz={me.user.time_zone} />) : rows.map((p) => <ProgRow key={p.id} p={p} />)}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function DogRow({ d, tz }) {
  const { t } = useTranslation();
  const overdue = d.next_visit && d.next_visit.scheduled_local.slice(0, 10) < todayISO();
  return (
    <li>
      <Link to={`/dogs/${d.id}`} data-testid={`dog-row-${d.number}`} className="lift block rounded-2xl border bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-display text-lg font-bold">{d.name}</span>
          <Tag>{d.number}</Tag>
          {d.is_demo && <DemoBadge short />}
          <span className="ml-auto">{d.programme_status ? <StatusChip status={d.programme_status} /> : <StatusChip status="no_response" label={t("practice.no_programme")} />}</span>
        </div>
        <div className="mt-2 grid gap-1 text-sm text-muted-foreground sm:grid-cols-4">
          <span>{t("common.responsible")}: <b className="text-foreground">{d.responsible_name}</b></span>
          <span>{t("practice.latest")}: {fmtDateTime(d.latest_activity, tz)}</span>
          <span>{t("practice.next")}: {overdue ? <b className="text-[hsl(0_55%_34%)]">{t("common.overdue_since", { date: fmtDate(d.next_visit.scheduled_local.slice(0, 10)) })}</b> : d.next_visit ? fmtLocal(d.next_visit.scheduled_local, d.next_visit.time_zone) : d.next_step || "—"}</span>
          <span>{t("practice.counts", { programmes: d.programme_count, reviews: d.open_reviews })}</span>
        </div>
        {d.directions.length > 0 && <div className="mt-2 flex gap-1">{d.directions.map((x) => <Tag key={x}>{x}</Tag>)}</div>}
      </Link>
    </li>
  );
}

function ProgRow({ p }) {
  const { t } = useTranslation();
  return (
    <li>
      <Link to={`/programmes/${p.id}`} data-testid={`programme-row-${p.number}`} className="lift block rounded-2xl border bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-display text-lg font-bold">{p.title || p.number}</span><Tag>{p.number}</Tag>{p.is_demo && <DemoBadge short />}
          {p.archived && <StatusChip status="archived" label={t("status.archived")} />}
          <span className="ml-auto"><StatusChip status={p.status} /></span>
        </div>
        <div className="mt-2 grid gap-1 text-sm text-muted-foreground sm:grid-cols-4">
          <span>{p.dog_name}</span><span>{t("common.responsible")}: <b className="text-foreground">{p.responsible_name}</b></span>
          <span>{t("programme.review_date")}: {p.review_date ? fmtDate(p.review_date) : "—"}</span>
          <span>{p.directions.join(" · ")} · {t("practice.stages", { count: p.stage_count })}</span>
        </div>
      </Link>
    </li>
  );
}
